/**
 * Communications hub (mobile C6).
 *
 * Entry point for the messaging module: compose, templates, automations, plus a
 * recent message log (GET /api/communications) with channel + status badges.
 *
 * Messaging identities are configured under Account → Comms; not duplicated here.
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw styles.
 */
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { listCommunications, type CommunicationRow } from '../../src/api/comms';
import { Screen, Text, Badge, Card, Button, ListRow } from '../../src/components/ui';
import { theme, type StatusKey } from '../../src/theme';
import { extractErrorMessage, formatDateTime, humanize } from '../../src/lib/format';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Communication status → badge. */
function commStatusBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'sent':
    case 'delivered':
      return { key: 'paid', label: humanize(status) };
    case 'queued':
    case 'scheduled':
      return { key: 'sent', label: humanize(status) };
    case 'skipped':
      return { key: 'draft', label: 'Skipped' };
    case 'failed':
    case 'bounced':
      return { key: 'overdue', label: humanize(status) };
    default:
      return { key: 'draft', label: humanize(status) || status };
  }
}

function channelLabel(channel: string): string {
  if (channel === 'sms') return 'SMS';
  if (channel === 'email') return 'Email';
  return humanize(channel);
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function MessagesScreen() {
  const router = useRouter();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['communications'],
    queryFn: listCommunications,
    staleTime: 30_000,
  });

  const rows = data ?? [];

  return (
    <Screen scroll>
      <Text variant="title">Communications</Text>

      <Button
        title="Compose"
        onPress={() => router.push('/(app)/messages/compose')}
        accessibilityLabel="Compose a new message"
      />

      <View style={styles.list}>
        <ListRow
          title="Templates"
          subtitle="Reusable email & SMS templates"
          onPress={() => router.push('/(app)/messages/templates')}
        />
        <ListRow
          title="Automations"
          subtitle="Rules that send messages automatically"
          onPress={() => router.push('/(app)/messages/automations')}
        />
      </View>

      <Text variant="label" color="secondary">
        Recent messages
      </Text>

      {isLoading ? (
        <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading messages" />
      ) : isError ? (
        <Card>
          <Text variant="body" color="danger" accessibilityRole="alert">
            {extractErrorMessage(error, 'Failed to load messages.')}
          </Text>
          <Button
            title="Retry"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={() => refetch()}
            accessibilityLabel="Retry loading messages"
          />
        </Card>
      ) : rows.length === 0 ? (
        <Text variant="body" color="secondary">
          No messages yet. Tap Compose to send your first.
        </Text>
      ) : (
        <View style={styles.list}>
          {rows.map((c: CommunicationRow) => {
            const b = commStatusBadge(c.status);
            const recipient = c.to_address ?? humanize(c.direction);
            const preview = c.subject?.trim() || c.body?.trim() || '—';
            return (
              <ListRow
                key={c.id}
                title={`${channelLabel(c.channel)} · ${recipient}`}
                subtitle={`${formatDateTime(c.created_at)} · ${preview}`}
                right={<Badge status={b.key} label={b.label} />}
              />
            );
          })}
        </View>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  list: { gap: theme.spacing.sm },
});
